import { Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from '../../contexts/TranslationContext';
import { GLOBAL_MARKET_SELECTION } from '../../features/pricing/useCountryPreference';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { tokens } from '../../theme/tokens';
import { Text } from '../primitives/Text';

// The catalog prices exactly two markets today (see the backend's
// `market: 'colombia' | 'global'`), so offering a full country list would
// promise localization that does not exist. Add an entry here when a market
// actually gets its own currency.
const MARKET_OPTIONS = [
  { value: 'CO', labelKey: 'landing.pricing.countrySelector.colombia' },
  { value: GLOBAL_MARKET_SELECTION, labelKey: 'landing.pricing.countrySelector.international' },
] as const;

export interface CountrySelectorProps {
  /** The country currently pricing the page, however it was resolved. */
  activeCountry: string | null;
  onSelect: (value: string) => void;
}

/**
 * Lets the buyer correct the detected market.
 *
 * Geo-IP is a guess: a Colombian on a VPN, or someone travelling, gets the
 * wrong currency and otherwise has no recourse. This is the manual override.
 */
export const CountrySelector = ({ activeCountry, onSelect }: CountrySelectorProps) => {
  const { t } = useTranslation();
  const normalized = activeCountry?.trim().toUpperCase() ?? null;
  const activeValue = normalized === null ? null : normalized === 'CO' ? 'CO' : 'ZZ';

  return (
    <View style={styles.wrapper}>
      <Text variant="caption" color={colors.text.tertiary}>
        {t('landing.pricing.countrySelector.label')}
      </Text>
      <View style={styles.container}>
        {MARKET_OPTIONS.map((option) => {
          const isSelected = activeValue === option.value;
          const label = t(option.labelKey);

          return (
            <Pressable
              key={option.value}
              testID={`pricing_country_${option.value}`}
              onPress={isSelected ? undefined : () => onSelect(option.value)}
              style={[styles.option, isSelected && styles.activeOption]}
              accessibilityRole="button"
              accessibilityLabel={label}
              accessibilityState={{ selected: isSelected }}
            >
              <Text variant="bodySmall" style={[styles.label, isSelected && styles.activeLabel]}>
                {label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  container: {
    flexDirection: 'row',
    gap: spacing.xs,
    backgroundColor: colors.surface.row,
    borderRadius: tokens.radius.full,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    padding: spacing.xs,
  },
  option: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeOption: {
    backgroundColor: colors.primary[500],
  },
  label: {
    fontWeight: '500',
    color: colors.text.secondary,
  },
  activeLabel: {
    color: colors.text.inverse,
    fontWeight: '600',
  },
});
